import React from "react";
import { Card, Avatar, Tag, Badge, Tooltip } from "antd";
import {
  UserOutlined,
  TeamOutlined,
  MailOutlined,
  PhoneOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import { HierarchyUser } from "../../../utils/api/hierarchyAPI";
import "./EmployeeCard.scss";

interface EmployeeCardProps {
  user: HierarchyUser;
  onAssignManager?: (user: HierarchyUser) => void;
  onClick?: (user: HierarchyUser) => void;
  draggable?: boolean;
  onDragStart?: (user: HierarchyUser) => void;
  onDragEnd?: () => void;
  onDrop?: (targetUser: HierarchyUser, draggedUser: HierarchyUser) => void;
}

const EmployeeCard: React.FC<EmployeeCardProps> = ({
  user,
  onAssignManager,
  onClick,
  draggable = true,
  onDragStart,
  onDragEnd,
  onDrop,
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const draggedUserRef = React.useRef<HierarchyUser | null>(null);

  const fullName =
    `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown";
  const position = user.position?.positionName || "No Position";
  const teamSize = user.teamSize || 0;

  const handleDragStart = (e: React.DragEvent) => {
    if (!draggable) return;

    setIsDragging(true);
    draggedUserRef.current = user;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("userId", user._id);

    if (onDragStart) {
      onDragStart(user);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    draggedUserRef.current = null;

    if (onDragEnd) {
      onDragEnd();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const draggedUserId = e.dataTransfer.getData("userId");

    // Don't allow dropping on self
    if (draggedUserId === user._id) {
      return;
    }

    if (onDrop && draggedUserRef.current) {
      onDrop(user, draggedUserRef.current);
    }

    // Trigger assign manager modal
    if (onAssignManager && draggedUserRef.current) {
      onAssignManager(draggedUserRef.current);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Prevent click during drag
    if (isDragging) return;

    if (onClick) {
      onClick(user);
    }
  };

  const getLevelColor = (level: number) => {
    const colors = ["gold", "blue", "green", "orange", "purple", "cyan"];
    return colors[level % colors.length];
  };

  return (
    <Card
      className={`employee-card ${isDragging ? "dragging" : ""} ${
        isDragOver ? "drag-over" : ""
      }`}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      hoverable
    >
      <div className="card-content">
        {/* Avatar Section */}
        <div className="avatar-section">
          <Badge
            count={
              user.isManager ? (
                <CrownOutlined style={{ color: "#faad14" }} />
              ) : (
                0
              )
            }
            offset={[-8, 8]}
          >
            <Avatar
              size={64}
              src={user.profilePicture}
              icon={<UserOutlined />}
              className="user-avatar"
            />
          </Badge>
          <Tag
            color={getLevelColor(user.hierarchyLevel || 0)}
            className="level-tag"
          >
            {user.hierarchyLevel === 0
              ? "Top Level"
              : `Level ${user.hierarchyLevel}`}
          </Tag>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <h4 className="user-name">{fullName}</h4>
          <p className="user-position">{position}</p>

          <div className="user-details">
            {user.email && (
              <Tooltip title={user.email}>
                <div className="detail-item">
                  <MailOutlined />
                  <span className="detail-text">{user.email}</span>
                </div>
              </Tooltip>
            )}
            {user.phone && (
              <Tooltip title={user.phone}>
                <div className="detail-item">
                  <PhoneOutlined />
                  <span className="detail-text">{user.phone}</span>
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Stats Section */}
        {user.isManager && teamSize > 0 && (
          <div className="stats-section">
            <div className="stat-item">
              <TeamOutlined />
              <span>{teamSize} team members</span>
            </div>
          </div>
        )}

        {/* Manager Info */}
        {user.managerId && (
          <div className="manager-info">
            <span className="manager-label">Reports to:</span>
            <span className="manager-name">
              {typeof user.managerId === "object"
                ? `${user.managerId.firstName} ${user.managerId.lastName}`
                : "Manager"}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
};

export default EmployeeCard;
